import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { api } from '../../../../singleton/axios';
import { Watchlist } from './useWatchlists';

interface RemoveStockData {
  holdingId: string;
  ticker: string; // For display in success message
}

async function removeStockFromWatchlist(watchlistId: string, holdingId: string): Promise<void> {
  try {
    await api.delete(`/stock-watchlists/${watchlistId}/holdings/${holdingId}`);
  } catch (error: any) {
    throw new Error(
      error.response?.data?.error ||
      `Failed to remove stock from watchlist: ${error.response?.status || 'Unknown error'}`
    );
  }
}

export function useRemoveStockFromWatchlist(watchlistId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RemoveStockData) => removeStockFromWatchlist(watchlistId, data.holdingId),

    // Optimistic update - immediately remove stock from cache before API call completes
    onMutate: async (variables) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: ['watchlist', watchlistId] });

      // Snapshot the previous value for rollback
      const previousWatchlist = queryClient.getQueryData<Watchlist>(['watchlist', watchlistId]);

      // Optimistically update the cache
      queryClient.setQueryData<Watchlist>(['watchlist', watchlistId], (old) => {
        if (!old) return old;

        // Filter out the removed holding
        const updatedHoldings = old.holdings.filter((holding) => holding.id !== variables.holdingId);

        return {
          ...old,
          holdings: updatedHoldings,
          holdings_count: updatedHoldings.length,
        };
      });

      // Return context with previous value for rollback
      return { previousWatchlist };
    },

    onSuccess: (_, variables) => {
      // Invalidate queries to get fresh data from server (confirming the optimistic update)
      queryClient.invalidateQueries({ queryKey: ['watchlist', watchlistId] });
      queryClient.invalidateQueries({ queryKey: ['watchlists'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-watchlist'] }); // Update dashboard too
      queryClient.invalidateQueries({ queryKey: ['watchlist-earnings'] }); // Earnings reads the same holdings
      queryClient.invalidateQueries({ queryKey: ['watchlist-earnings-feed'] });
      queryClient.invalidateQueries({ queryKey: ['watchlist-earnings-feed-head'] });
      // Matches the add path — without this a removed ticker lingers on the
      // charts/compare/chat tiles until its own staleTime lapses.
      queryClient.invalidateQueries({ queryKey: ['chartsWatchlist'] });

      // Show success notification
      enqueueSnackbar(`${variables.ticker} removed from watchlist successfully!`, {
        variant: 'success',
        autoHideDuration: 3000,
      });
    },

    onError: (error, variables, context) => {
      // Rollback optimistic update on error
      if (context?.previousWatchlist) {
        queryClient.setQueryData(['watchlist', watchlistId], context.previousWatchlist);
      }

      // Show error notification
      enqueueSnackbar(error.message || 'Failed to remove stock from watchlist:', {
        variant: 'error',
        autoHideDuration: 5000,
      });
    },
  });
}